import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { toDateForDb } from "@/shared/lib/date-time";
import { Button } from "@devhop/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { useRef } from "react";
import {
  useAuditPlanDetailQuery,
  useCreateAuditPlan,
  useUpdateAuditPlan,
} from "../api/queries";
import {
  AuditPlanFormTour,
  useAuditPlanFormTour,
} from "../tour/AuditPlanFormTour";
import {
  AuditPlanForm,
  type AuditPlanFormRef,
  type AuditPlanFormValues,
} from "../ui/AuditPlanForm";

export function AuditPlanCreatePage() {
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isEdit = pathname.includes("/edit");
  const tour = useAuditPlanFormTour();

  // Extract ID from pathname for edit route
  const id = isEdit
    ? pathname.match(/\/internal-audit\/([^/]+)\/edit/)?.[1]
    : undefined;

  const nav = useNavigate();

  const canCreate = useActionPermission(["internal_audit:create"]);
  const canUpdate = useActionPermission(["internal_audit:update"]);
  const hasPermission = isEdit ? canUpdate : canCreate;

  const createAuditPlan = useCreateAuditPlan();
  const updateAuditPlan = useUpdateAuditPlan(id ?? "");
  const auditPlanDetail = useAuditPlanDetailQuery(id ?? "");

  const formRef = useRef<AuditPlanFormRef | null>(null);

  if (!hasPermission) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ທ່ານບໍ່ມີສິດເຂົ້າເຖິງ</p>
          </div>
        </Main>
      </>
    );
  }

  const handleSubmit = async (data: AuditPlanFormValues) => {
    // Prepare common data
    const baseData = {
      name: data.name.trim(),
      description: data.description?.trim() || undefined,
      auditTypeId: data.auditTypeId,
      departmentId: data.departmentId,
      auditorPositionId: data.auditorPositionId,
      frequency: data.frequency,
      status: data.status,
      isActive: data.isActive ?? true,
    };

    // Handle optional fields - only include if they have values
    const optionalFields: {
      sopId?: string;
      qcStandardId?: string;
      checklistId?: string;
      kpiId?: string;
    } = {};

    if (data.sopId?.trim()) {
      optionalFields.sopId = data.sopId.trim();
    }

    if (data.qcStandardId?.trim()) {
      optionalFields.qcStandardId = data.qcStandardId.trim();
    }

    if (data.checklistId?.trim()) {
      optionalFields.checklistId = data.checklistId.trim();
    }

    if (data.kpiId?.trim()) {
      optionalFields.kpiId = data.kpiId.trim();
    }

    if (isEdit && id) {
      // Update existing audit plan
      const updateData: typeof baseData &
        typeof optionalFields & { nextAuditDate?: Date } = {
        ...baseData,
        ...optionalFields,
      };

      if (data.nextAuditDate) {
        const dateObj = toDateForDb(`${data.nextAuditDate}T00:00:00`);
        if (dateObj) {
          updateData.nextAuditDate = dateObj;
        }
      }

      try {
        await updateAuditPlan.mutateAsync(updateData);
        nav({ to: `/app/internal-audit/${id}` });
      } catch (error) {
        // Error is handled by mutation's onError callback
        // Don't re-throw to avoid unhandled promise rejection
        if (process.env.NODE_ENV === "development") {
          console.error("Update error:", error);
        }
      }
    } else {
      // Create new audit plan
      if (!data.nextAuditDate) {
        // This should be caught by form validation, but handle it just in case
        console.error("nextAuditDate is required");
        return;
      }

      const dateObj = toDateForDb(`${data.nextAuditDate}T00:00:00`);
      if (!dateObj) {
        console.error("Invalid date format");
        return;
      }

      const createData: typeof baseData &
        typeof optionalFields & { nextAuditDate: Date } = {
        ...baseData,
        ...optionalFields,
        nextAuditDate: dateObj,
      };

      // Log data being sent in development
      if (process.env.NODE_ENV === "development") {
        // Log what will be sent to FormData
        const year = createData.nextAuditDate.getFullYear();
        const month = String(createData.nextAuditDate.getMonth() + 1).padStart(
          2,
          "0",
        );
        const day = String(createData.nextAuditDate.getDate()).padStart(2, "0");
        console.log("[CREATE AUDIT PLAN] Sending data:", {
          ...createData,
          nextAuditDate: `${year}-${month}-${day}`, // Show what will be in FormData
          nextAuditDateISO: createData.nextAuditDate.toISOString(), // Show ISO for reference
        });
      }

      try {
        const result = await createAuditPlan.mutateAsync(createData);

        // Navigate to detail page on success
        if (result?.value?.id) {
          nav({ to: `/app/internal-audit/${result.value.id}` });
        }
      } catch (error) {
        // Error is handled by mutation's onError callback
        // Don't re-throw to avoid unhandled promise rejection
        if (process.env.NODE_ENV === "development") {
          console.error("Create error:", error);
        }
      }
    }
  };

  return (
    <>
      <Header />

      <Main>
        <AuditPlanFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">
                  {isEdit ? "ແກ້ໄຂຜະແນການກວດສອບ" : "ສ້າງຜະແນການກວດສອບໃໝ່"}
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground">
                {isEdit ? "ແກ້ໄຂຂໍ້ມູນຜະແນການກວດສອບ" : "ສ້າງຜະແນການກວດສອບພາຍໃນໃໝ່"}
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/internal-audit",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    filters: undefined,
                  },
                })
              }
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປລາຍການ
            </Button>
          </div>

          {isEdit && auditPlanDetail.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          ) : (
            <div className="rounded-xl border bg-card p-6">
              <AuditPlanForm
                initialData={auditPlanDetail.data}
                onSubmit={handleSubmit}
                isLoading={
                  createAuditPlan.isPending || updateAuditPlan.isPending
                }
                formRef={formRef}
              />
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
